'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '../lib/supabase/client';

// ── Three roles ───────────────────────────────────────────────────────────────
export type UserRole = 'Administrateur' | 'Broker' | 'Broker Senior' | 'Visiteur';

export interface UserProfile {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  role: UserRole;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  authenticated: boolean;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  isBroker: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const supabase = createClient();

  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  // Start as true — we haven't resolved auth yet
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Guard: prevent concurrent profile fetches
  const fetchingRef = useRef(false);
  // Guard: prevent state updates after unmount
  const mountedRef = useRef(true);
  // Track the last user ID we fetched a profile for — avoid redundant fetches on TOKEN_REFRESHED
  const lastFetchedUserIdRef = useRef<string | null>(null);

  // ── Fetch profile from user_profiles ────────────────────────────────────
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, nom, prenom, role')
        .eq('id', userId)
        .maybeSingle();

      if (error || !data) {
        console.warn('[AuthContext] fetchProfile — no profile found for', userId, error?.message);
        return null;
      }

      // Accept all valid roles from the DB enum
      const validRoles: UserRole[] = ['Administrateur', 'Broker Senior', 'Broker', 'Visiteur'];
      const role: UserRole = validRoles.includes(data.role as UserRole)
        ? (data.role as UserRole)
        : 'Visiteur';

      console.log('[AuthContext] fetchProfile — id:', userId, '| role:', role);

      return {
        id: data.id,
        email: data.email || '',
        nom: data.nom || '',
        prenom: data.prenom || '',
        role,
      };
    } catch (err: any) {
      console.error('[AuthContext] fetchProfile — unexpected error:', err?.message);
      return null;
    }
  }, [supabase]);

  // ── Single source of truth: onAuthStateChange ────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mountedRef.current) return;

      console.log('[AuthContext] onAuthStateChange event:', event, '| user:', s?.user?.email ?? 'none');

      // User signed out or no session
      if (event === 'SIGNED_OUT' || !s?.user) {
        setUser(null);
        setSession(null);
        setUserProfile(null);
        lastFetchedUserIdRef.current = null;
        setLoading(false);
        return;
      }

      // For TOKEN_REFRESHED: only update session/user, do NOT re-fetch profile
      // unless we don't have one yet. This prevents overwriting a valid admin profile.
      if (event === 'TOKEN_REFRESHED') {
        setSession(s);
        setUser(s.user);
        // Only fetch profile if we don't have one (e.g. after a page reload)
        if (lastFetchedUserIdRef.current === s.user.id) {
          // Profile already loaded for this user — skip re-fetch
          setLoading(false);
          return;
        }
      }

      // For INITIAL_SESSION, SIGNED_IN — load profile
      if (
        event === 'INITIAL_SESSION' ||
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED'
      ) {
        // Prevent concurrent fetches
        if (fetchingRef.current) return;
        fetchingRef.current = true;

        setSession(s);
        setUser(s.user);

        try {
          const profile = await fetchProfile(s.user.id);
          if (mountedRef.current) {
            setUserProfile(profile);
            lastFetchedUserIdRef.current = s.user.id;
          }
        } finally {
          fetchingRef.current = false;
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, supabase]);

  // ── signIn ───────────────────────────────────────────────────────────────
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      const msg = error.message || '';
      const status = (error as any)?.status;

      if (status === 429 || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        const err = new Error('RATE_LIMIT_429') as any;
        err.isRateLimit = true;
        throw err;
      }
      if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
        throw new Error('Identifiants incorrects. Vérifiez votre email et mot de passe.');
      }
      if (msg.toLowerCase().includes('email not confirmed')) {
        throw new Error("Votre adresse email n'a pas encore été confirmée.");
      }
      throw new Error(msg || 'Erreur de connexion.');
    }

    if (!data.session?.user) {
      throw new Error('Connexion échouée. Veuillez réessayer.');
    }

    // Set session/user immediately for snappy UX
    setSession(data.session);
    setUser(data.session.user);

    // Fetch profile once here; mark as fetched so TOKEN_REFRESHED won't overwrite it
    const profile = await fetchProfile(data.session.user.id);
    if (mountedRef.current) {
      setUserProfile(profile);
      lastFetchedUserIdRef.current = data.session.user.id;
    }
    // onAuthStateChange SIGNED_IN will fire but fetchingRef/lastFetchedUserIdRef guard it
  };

  // ── signUp ───────────────────────────────────────────────────────────────
  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: 'Visiteur',
          ...metadata,
        },
      },
    });

    if (error) {
      if (error.message.toLowerCase().includes('already registered') || error.message.toLowerCase().includes('already exists')) {
        throw new Error('Un compte existe déjà avec cet email.');
      }
      throw new Error(error.message || 'Erreur lors de la création du compte.');
    }

    // If session returned immediately (email confirmation disabled)
    if (data.session?.user) {
      setSession(data.session);
      setUser(data.session.user);
      const profile = await fetchProfile(data.session.user.id);
      if (mountedRef.current) {
        setUserProfile(profile);
        lastFetchedUserIdRef.current = data.session.user.id;
      }
    }
  };

  // ── signOut ──────────────────────────────────────────────────────────────
  const signOut = async () => {
    await supabase.auth.signOut();
    // onAuthStateChange SIGNED_OUT will clear state, but clear immediately too
    setUser(null);
    setSession(null);
    setUserProfile(null);
    lastFetchedUserIdRef.current = null;
  };

  // ── refreshProfile ───────────────────────────────────────────────────────
  const refreshProfile = async () => {
    if (!user?.id) return;
    const profile = await fetchProfile(user.id);
    if (mountedRef.current) {
      setUserProfile(profile);
      lastFetchedUserIdRef.current = user.id;
    }
  };

  const isAdmin = userProfile?.role === 'Administrateur';
  const isBroker = userProfile?.role === 'Broker' || userProfile?.role === 'Broker Senior';
  const authenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      authenticated,
      userProfile,
      isAdmin,
      isBroker,
      signUp,
      signIn,
      signOut,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
